import { Button, DividerWithLabel } from '@payaca/ambrosia';
import { FC, useMemo, useRef } from 'react';

import { PipelineStage } from '@/api/queries/pipelines/useGetPipeline';
import useDraftUpdatePipelineStage from '@/ui/pages/pipelineConfigPage/api/mutations/useDraftUpdatePipelineStage';
import PipelineGuardOrFieldset from '@/ui/pages/pipelineConfigPage/components/guards/PipelineGuardOrFieldset';
import {
  createDefaultAndGuard,
  createDefaultOrGuard,
  formStateToGraphqlInput,
  graphqlGuardToFormState,
  PipelineGuardOrFormState,
} from '@/ui/pages/pipelineConfigPage/utils/pipelineGuardConversions';

type Props = {
  pipelineId?: string;
  stage: PipelineStage;
  stageIndex: number;
};

const PipelineStageGuards: FC<Props> = ({ pipelineId, stage, stageIndex }) => {
  const { mutate: updateStage } = useDraftUpdatePipelineStage({
    pipelineId,
  });

  // Hold the previous form state so `graphqlGuardToFormState` can reuse
  // tempIdentifiers across re-derivations. Each draft mutation's
  // optimistic update bumps `stage.guard`, and without identifier reuse
  // every keystroke would mint fresh React keys and remount the row's
  // input — which is what dropped focus mid-typing on a comparisonValue.
  const previousGuardFormStateRef = useRef<PipelineGuardOrFormState | null>(
    null
  );
  const guardFormState = useMemo(() => {
    const next = graphqlGuardToFormState(
      stage.guard,
      previousGuardFormStateRef.current
    );
    previousGuardFormStateRef.current = next;
    return next;
  }, [stage.guard]);

  const handleGuardChange = (newGuard: PipelineGuardOrFormState | null) => {
    updateStage({
      id: stage.publicId,
      guard: formStateToGraphqlInput(newGuard),
    });
  };

  const handleAddGuard = () => {
    if (guardFormState?.children.length) {
      handleGuardChange({
        ...guardFormState,
        children: [...guardFormState.children, createDefaultAndGuard()],
      });
    } else {
      handleGuardChange(createDefaultOrGuard());
    }
  };

  // First stage cannot have guards
  if (stageIndex === 0) {
    return (
      <p className="text-sm text-gray-500">First stage cannot have guards</p>
    );
  }

  return (
    <>
      <h4>Only if:</h4>
      {guardFormState && (
        <PipelineGuardOrFieldset
          value={guardFormState}
          onChange={handleGuardChange}
        />
      )}
      {guardFormState && <DividerWithLabel label="or" />}
      <Button
        className="w-full"
        variant="tertiary"
        size="sm"
        onClick={handleAddGuard}
      >
        Add guard
      </Button>
    </>
  );
};

export default PipelineStageGuards;
